import { inferAdditionalFields } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';

/**
 * Better Auth browser client.
 *
 * Sessions, sign-in, sign-out and the OAuth handshake go through Better Auth.
 * Everything else — organizations, plans, usage, billing — goes through the
 * REST API in `api.ts`, because that is where the tenant authorisation and
 * audit logging live.
 *
 * The organization plugin client is deliberately not used: it would let the
 * browser call organization endpoints that bypass our own middleware.
 */
export const authClient = createAuthClient({
  baseURL: window.location.origin,
  basePath: '/api/auth',
  plugins: [
    /*
     * Fields the server added to `user`, declared again for the client's types.
     *
     * Better Auth can infer these from the server instance, but only where both
     * halves share a compilation — and the console is built by its own tsconfig
     * that does not see `src/`. Declaring the shape explicitly is the documented
     * alternative for exactly that arrangement.
     *
     * It is a type declaration, not a validator: what the server accepts is
     * decided in modules/auth/auth.ts, and this only stops the console sending a
     * field TypeScript would otherwise reject.
     */
    inferAdditionalFields({
      user: {
        phoneNumber: { type: 'string' },
      },
    }),
  ],
});

export const { useSession, signIn, signUp, signOut } = authClient;

export interface AuthProviders {
  emailAndPassword: boolean;
  social: string[];
}

/** Which sign-in methods the server actually has configured. */
export async function fetchAuthProviders(): Promise<AuthProviders> {
  const response = await fetch('/api/auth-providers', { credentials: 'include' });
  if (!response.ok) return { emailAndPassword: true, social: [] };
  return (await response.json()) as AuthProviders;
}

/** Better Auth returns errors as `{ error: { message } }` rather than throwing. */
export function authErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.length > 0) return message;
  }
  return fallback;
}
